import { themeAtom } from "../../shared/atoms";
import { Bq1GeneralInformationM } from "../models/bq1-general-information.model";
import { InputCell } from "../services/baltic-forms.utils";
import { useAtomValue } from "jotai";
import React from "react";

export function Bq1Tonnages(
  form: Bq1GeneralInformationM,
  updateForm: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void,
  toggleComments: (inputId: string) => void,
  isCommentsVisible: (inputId: string) => boolean,
  readonly: boolean,
) {
  const theme = useAtomValue(themeAtom);

  return (
    <div>
      <h2 className="mb-2 mt-[120px] text-lg font-semibold">Tonnages</h2>
      <br />
      <div className="mb-8 grid grid-cols-4 gap-4">
        {/* Gross Tonnage (GRT) */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Gross Tonnage (GRT)</label>
          <InputCell
            disabled={readonly}
            name="grossTonnageGRT"
            value={form.grossTonnageGRT}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>

        {/* Net Registered Tonnage */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Net Registered Tonnage (NRT)</label>
          <InputCell
            disabled={readonly}
            name="netRegisteredTonnageNRT"
            value={form.netRegisteredTonnageNRT}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>
      </div>

      <div className="mb-8 grid grid-cols-4 gap-4">
        {/* Suez Canal GT / NT */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Suez Canal Gross Tonnage (SCGT)</label>
          <InputCell
            disabled={readonly}
            name="suezCanalGrossTonnageSCGT"
            value={form.suezCanalGrossTonnageSCGT}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("suezCanalGrossTonnageSCGTComments")}
            >
              Comments
            </button>
          </label>
          {(form.suezCanalGrossTonnageSCGTComments || isCommentsVisible("suezCanalGrossTonnageSCGTComments")) && (
            <textarea
              disabled={readonly}
              name="suezCanalGrossTonnageSCGTComments"
              value={form.suezCanalGrossTonnageSCGTComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">Suez Canal Net Tonnage (SCNT)</label>
          <InputCell
            disabled={readonly}
            name="suezCanalNetTonnageSCNT"
            value={form.suezCanalNetTonnageSCNT}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("suezCanalNetTonnageSCNTComments")}
            >
              Comments
            </button>
          </label>
          {(form.suezCanalNetTonnageSCNTComments || isCommentsVisible("suezCanalNetTonnageSCNTComments")) && (
            <textarea
              disabled={readonly}
              name="suezCanalNetTonnageSCNTComments"
              value={form.suezCanalNetTonnageSCNTComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      <div className="mb-8 grid grid-cols-4 gap-4">
        {/* Panama Canal Net Tonnage (PCNT) */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Panama Canal Net Tonnage (PCNT)</label>
          <InputCell
            disabled={readonly}
            name="panamaCanalNetTonnagePCNT"
            value={form.panamaCanalNetTonnagePCNT}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>
      </div>

      {/* Loadline Info */}
      <h2 className="mb-2 text-lg font-semibold">Loadline Information</h2>

      {/* Summer */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Summer Deadweight (SDWT)</label>
          <InputCell
            disabled={readonly}
            name="summerDeadweight"
            value={form.summerDeadweight}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>
        <div className="col-span-1">
          <label className="block text-sm font-bold">Summer Draft</label>
          <InputCell
            disabled={readonly}
            name="summerDraft"
            value={form.summerDraft}
            onChange={updateForm}
            trailingText="Metres"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">Tonnes per Centimetre Immersion (TPC) at Summer Draft:</label>
          <InputCell
            disabled={readonly}
            name="tpcAtSummerDraft"
            value={form.tpcAtSummerDraft}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>
      </div>

      {/* Winter */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Winter Deadweight (SDWT)</label>
          <InputCell
            disabled={readonly}
            name="winterDeadweight"
            value={form.winterDeadweight}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">Winter Draft</label>
          <InputCell
            disabled={readonly}
            name="winterDraft"
            value={form.winterDraft}
            onChange={updateForm}
            trailingText="Metres"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">TPC Immersion at Winter Draft (Freeboard):</label>
          <InputCell
            disabled={readonly}
            name="tpcImmersionWinterDraft"
            value={form.tpcImmersionWinterDraft}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>
      </div>

      {/* Winter North Atlantic */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Winter North Atlantic Deadweight:</label>
          <InputCell
            disabled={readonly}
            name="winterNorthAtlanticDeadweight"
            value={form.winterNorthAtlanticDeadweight}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">Winter North Atlantic Draft:</label>
          <InputCell
            disabled={readonly}
            name="winterNorthAtlanticDraft"
            value={form.winterNorthAtlanticDraft}
            onChange={updateForm}
            trailingText="Metres"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">TPC Immersion at Winter North Atlantic Draft (Freeboard):</label>
          <InputCell
            disabled={readonly}
            name="tpcImmersionWinterNorthAtlanticDraft"
            value={form.tpcImmersionWinterNorthAtlanticDraft}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>
      </div>

      {/* Fresh Water */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Fresh Water Deadweight:</label>
          <InputCell
            disabled={readonly}
            name="freshWaterDeadweight"
            value={form.freshWaterDeadweight}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">Fresh Water Draft:</label>
          <InputCell
            disabled={readonly}
            name="freshWaterDraft"
            value={form.freshWaterDraft}
            onChange={updateForm}
            trailingText="Metres"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">TPC Immersion at Fresh Water Draft (Freeboard):</label>
          <InputCell
            disabled={readonly}
            name="tpcImmersionFreshWaterDraft"
            value={form.tpcImmersionFreshWaterDraft}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>
      </div>

      {/* Tropical */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Tropical Deadweight:</label>
          <InputCell
            disabled={readonly}
            name="tropicalDeadweight"
            value={form.tropicalDeadweight}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">Tropical Draft:</label>
          <InputCell
            disabled={readonly}
            name="tropicalDraft"
            value={form.tropicalDraft}
            onChange={updateForm}
            trailingText="Metres"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">TPC Immersion at Tropical Draft (Freeboard):</label>
          <InputCell
            disabled={readonly}
            name="tpcImmersionTropicalDraft"
            value={form.tpcImmersionTropicalDraft}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>
      </div>

      {/* Tropical Fresh Water */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Tropical Fresh Water Deadweight:</label>
          <InputCell
            disabled={readonly}
            name="tropicalFreshWaterDeadweight"
            value={form.tropicalFreshWaterDeadweight}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">Tropical Fresh Water Draft:</label>
          <InputCell
            disabled={readonly}
            name="tropicalFreshWaterDraft"
            value={form.tropicalFreshWaterDraft}
            onChange={updateForm}
            trailingText="Metres"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">TPC Immersion at Tropical Fresh Water Draft (Freeboard):</label>
          <InputCell
            disabled={readonly}
            name="tpcImmersionTropicalFreshDraft"
            value={form.tpcImmersionTropicalFreshDraft}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>
      </div>

      {/* Normal Ballast Condition */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">
            Normal Ballast Condition Deadweight:
            <br />
            &nbsp;
          </label>
          <InputCell
            disabled={readonly}
            name="normalBallastConditionDeadweight"
            value={form.normalBallastConditionDeadweight}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">
            Normal Ballast Condition Draft:
            <br />
            &nbsp;
          </label>
          <InputCell
            disabled={readonly}
            name="normalBallastConditionDraft"
            value={form.normalBallastConditionDraft}
            onChange={updateForm}
            trailingText="Metres"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("normalBallastConditionDraftComments")}
            >
              Comments
            </button>
          </label>
          {(form.normalBallastConditionDraftComments || isCommentsVisible("normalBallastConditionDraftComments")) && (
            <textarea
              disabled={readonly}
              name="normalBallastConditionDraftComments"
              value={form.normalBallastConditionDraftComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">
            TPC Immersion at Normal Ballast Condition Draft (Freeboard):
          </label>
          <InputCell
            disabled={readonly}
            name="tpcImmersionNormalBallastDraft"
            value={form.tpcImmersionNormalBallastDraft}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>
      </div>

      {/* Lightship */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Lightship Deadweight:</label>
          <InputCell
            disabled={readonly}
            name="lightshipDeadweight"
            value={form.lightshipDeadweight}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">Lightship Draft:</label>
          <InputCell
            disabled={readonly}
            name="lightshipDraft"
            value={form.lightshipDraft}
            onChange={updateForm}
            trailingText="Metres"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">TPC Immersion at Lightship Draft (Freeboard):</label>
          <InputCell
            disabled={readonly}
            name="tpcImmersionLightshipDraft"
            value={form.tpcImmersionLightshipDraft}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>
      </div>

      {/* Lakes */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Lakes Deadweight:</label>
          <InputCell
            disabled={readonly}
            name="lakesDeadweight"
            value={form.lakesDeadweight}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">Lakes Draft:</label>
          <InputCell
            disabled={readonly}
            name="lakesDraft"
            value={form.lakesDraft}
            onChange={updateForm}
            trailingText="Metres"
            inputType="number"
          />
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">TPC Immersion at Lakes Draft (Freeboard):</label>
          <InputCell
            disabled={readonly}
            name="tpcImmersionLakesDraft"
            value={form.tpcImmersionLakesDraft}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>
      </div>

      {/* Fresh Water Allowance (FWA) at Summer Draft: */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold"> Fresh Water Allowance (FWA) at Summer Draft:</label>
          <InputCell
            disabled={readonly}
            name="freshWaterAllowanceAtSummerDraft"
            value={form.freshWaterAllowanceAtSummerDraft}
            onChange={updateForm}
            trailingText="Milimeters"
            inputType="number"
          />
        </div>
      </div>

      {/* Under Keel Clearance */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-bold">
            What is the company guidelines for Under Keel Clearance (UKC) for this vessel?:
          </label>
          <textarea
            disabled={readonly}
            name="underKeelClearanceGuidelines"
            value={form.underKeelClearanceGuidelines}
            onChange={updateForm}
            className="mt-1 h-16 w-full resize-none rounded-md border p-2 focus:outline-none focus:ring"
          />
        </div>
      </div>

      <h2 className="mb-2 mt-[120px] text-lg font-semibold">Canal Transits</h2>

      {/* Fitted for Panama */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-bold">Is vessel fitted for transit of Panama canal?</label>
          <div className="mt-1 flex space-x-4">
            {["yes", "no"].map((val) => (
              <label key={val} className="inline-flex items-center space-x-1">
                <input
                  disabled={readonly}
                  type="radio"
                  name="fittedForPanama"
                  value={val}
                  checked={form.fittedForPanama === val}
                  onChange={updateForm}
                  className="form-radio text-green-600"
                />
                <span className="text-sm capitalize">{val}</span>
              </label>
            ))}
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("fittedForPanamaComments")}
            >
              Comments
            </button>
          </label>
          {(form.fittedForPanamaComments || isCommentsVisible("fittedForPanamaComments")) && (
            <textarea
              disabled={readonly}
              name="fittedForPanamaComments"
              value={form.fittedForPanamaComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Deadweight panama */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="mt-4 block text-sm font-bold">
            For Panama Canal suitable vessel, state deadweight all told on 39ft 6in / 12.039m (sg 0.9954):
          </label>
          <InputCell
            disabled={readonly}
            name="panamaDeadweightAllTold"
            value={form.panamaDeadweightAllTold}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>
      </div>

      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-2">
          {/* Deadweight turn radius? */}
          <label className="mt-4 block text-sm font-bold">
            Is Panama deadweight all told affected by vessel's bilge turn radius?:
          </label>
          <div className="mt-1 flex space-x-4">
            {["yes", "no", "n/a"].map((val) => (
              <label key={val} className="inline-flex items-center space-x-1">
                <input
                  disabled={readonly}
                  type="radio"
                  name="isPanamaDwAffectedBilgeTurnRadius"
                  value={val}
                  checked={form.isPanamaDwAffectedBilgeTurnRadius === val}
                  onChange={updateForm}
                  className="form-radio text-green-600"
                />
                <span className="text-sm capitalize">{val}</span>
              </label>
            ))}
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("isPanamaDwAffectedBilgeTurnRadiusComments")}
            >
              Comments
            </button>
          </label>
          {(form.isPanamaDwAffectedBilgeTurnRadiusComments ||
            isCommentsVisible("isPanamaDwAffectedBilgeTurnRadiusComments")) && (
            <textarea
              disabled={readonly}
              name="isPanamaDwAffectedBilgeTurnRadiusComments"
              value={form.isPanamaDwAffectedBilgeTurnRadiusComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Fitted for Suez canal? */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-bold">Is vessel fitted for transit of Suez canal?:</label>
          <div className="mt-1 flex space-x-4">
            {["yes", "no"].map((val) => (
              <label key={val} className="inline-flex items-center space-x-1">
                <input
                  disabled={readonly}
                  type="radio"
                  name="fittedForSuez"
                  value={val}
                  checked={form.fittedForSuez === val}
                  onChange={updateForm}
                  className="form-radio text-green-600"
                />
                <span className="text-sm capitalize">{val}</span>
              </label>
            ))}
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("fittedForSuezComments")}
            >
              Comments
            </button>
          </label>
          {(form.fittedForSuezComments || isCommentsVisible("fittedForSuezComments")) && (
            <textarea
              disabled={readonly}
              name="fittedForSuezComments"
              value={form.fittedForSuezComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Fitted for St. Lawrence seaway? */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-bold">Is vessel fitted for transit of St. Lawrence seaway?</label>
          <div className="mt-1 flex space-x-4">
            {["yes", "no"].map((val) => (
              <label key={val} className="inline-flex items-center space-x-1">
                <input
                  disabled={readonly}
                  type="radio"
                  name="fittedForStLawrence"
                  value={val}
                  checked={form.fittedForStLawrence === val}
                  onChange={updateForm}
                  className="form-radio text-green-600"
                />
                <span className="text-sm capitalize">{val}</span>
              </label>
            ))}
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("fittedForStLawrenceComments")}
            >
              Comments
            </button>
          </label>
          {(form.fittedForStLawrenceComments || isCommentsVisible("fittedForStLawrenceComments")) && (
            <textarea
              disabled={readonly}
              name="fittedForStLawrenceComments"
              value={form.fittedForStLawrenceComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="mt-4 block text-sm font-bold">
            For St. Lawrence seaway size vessel, state deadweight all told basis 26ft (7.92M) fresh water:
          </label>
          <InputCell
            disabled={readonly}
            name="stLawrenceDeadweightAllTold"
            value={form.stLawrenceDeadweightAllTold}
            onChange={updateForm}
            trailingText="Metric Tonnes"
            inputType="number"
          />
        </div>
      </div>
    </div>
  );
}
